import { db } from '../db/indexeddb'

/**
 * ID Resolution Helpers
 * Resolve between local Dexie UUIDs and Supabase cloud UUIDs.
 */

// ─── Tag ID Resolution ──────────────────────────────────────────────────────

/**
 * Resolve a local tagId (Dexie UUID) to its Supabase cloud UUID.
 * Returns null if the tag doesn't exist or hasn't been synced yet.
 */
export async function resolveCloudTagId(localTagId: string | null | undefined): Promise<string | null> {
  if (!localTagId) return null
  const tag = await db.getTag(localTagId)
  return tag?.cloudId ?? null
}

/**
 * Resolve a cloud tag id (Supabase UUID) to its local Dexie tag id.
 * First checks if the cloudTagId IS a local Dexie ID (unified identity),
 * then falls back to cloudId matching (legacy).
 */
export async function resolveLocalTagId(cloudTagId: string | null | undefined): Promise<string | null> {
  if (!cloudTagId) return null
  const directMatch = await db.tags.get(cloudTagId)
  if (directMatch) return directMatch.id
  const match = await db.tags.filter(t => t.cloudId === cloudTagId).first()
  return match?.id ?? null
}

/**
 * Build a mapping of local tag IDs → cloud tag IDs for bulk resolution.
 * Used when pushing tasks with tags to cloud.
 */
export async function buildLocalToCloudTagIdMap(): Promise<Record<string, string>> {
  const tags = await db.tags.filter(t => !!t.cloudId).toArray()
  const map: Record<string, string> = {}
  for (const tag of tags) {
    if (tag.cloudId) map[tag.id] = tag.cloudId
  }
  return map
}

/**
 * Build a mapping of cloud tag IDs → local tag IDs for bulk resolution.
 * Used when pulling tasks with tags from cloud.
 */
export async function buildCloudToLocalTagIdMap(): Promise<Record<string, string>> {
  const tags = await db.tags.toArray()
  const map: Record<string, string> = {}
  for (const tag of tags) {
    if (tag.cloudId) map[tag.cloudId] = tag.id
  }
  return map
}

// ─── Section ID Resolution ──────────────────────────────────────────────────

/**
 * Resolve a local sectionId (Dexie UUID) to its Supabase cloud UUID.
 * Returns null if the section doesn't exist or hasn't been synced yet.
 */
export async function resolveCloudSectionId(localSectionId: string | null | undefined): Promise<string | null> {
  if (!localSectionId) return null
  const section = await db.getSection(localSectionId)
  return section?.cloudId ?? null
}

/**
 * Resolve a cloud section_id (Supabase UUID) to its local Dexie section id.
 * First checks if the cloudSectionId IS a local Dexie ID (unified identity),
 * then falls back to cloudId matching (legacy).
 */
export async function resolveLocalSectionId(cloudSectionId: string | null | undefined): Promise<string | null> {
  if (!cloudSectionId) return null
  const directMatch = await db.sections.get(cloudSectionId)
  if (directMatch) return directMatch.id
  // Legacy fallback: search by cloudId (cloudId not indexed on sections, use filter)
  const match = await db.sections.filter(s => s.cloudId === cloudSectionId).first()
  return match?.id ?? null
}

/**
 * Resolve a local projectId (Dexie UUID) to its Supabase cloud UUID.
 * Returns null if the project doesn't exist or hasn't been synced yet.
 */
export async function resolveCloudProjectId(localProjectId: string | null | undefined): Promise<string | null> {
  if (!localProjectId) return null
  const project = await db.getProject(localProjectId)
  return project?.cloudId ?? null
}

/**
 * Resolve a cloud project_id (Supabase UUID) to its local Dexie project id.
 * First checks if the cloudProjectId IS a local Dexie ID (unified identity),
 * then falls back to cloudId matching (legacy).
 */
export async function resolveLocalProjectId(cloudProjectId: string | null | undefined): Promise<string | null> {
  if (!cloudProjectId) return null
  const directMatch = await db.projects.get(cloudProjectId)
  if (directMatch) return directMatch.id
  // Legacy fallback: search by cloudId (indexed query)
  const match = await db.projects.where('cloudId').equals(cloudProjectId).first()
  return match?.id ?? null
}
